import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useRoomContext } from '@livekit/components-react'
import { ParticipantEvent, type ScreenShareCaptureOptions } from 'livekit-client'
import { apiFailure } from '@/lib/apiFailure'
import { watchApi } from './api'
import { screenSource } from './rules'
import { useMediaQuery } from './useMediaQuery'
import type { StageFace } from './types'

/**
 * The screen picker's options: video only (a host's voice is their microphone), the browser's own tab left out so the
 * watch screen can't share itself, and switching what is shared allowed mid-share.
 */
const SCREEN_CAPTURE: ScreenShareCaptureOptions = {
  audio: false,
  selfBrowserSurface: 'exclude',
  surfaceSwitching: 'include',
  contentHint: 'detail',
}

/** A pointer that can aim (a mouse, a trackpad, a pen): a desktop browser, where sharing a screen makes sense. */
const FINE_POINTER = '(any-pointer: fine)'

export interface HostSourcesOptions {
  /** The stage this tab is live on (the presence's); null before a stage loads. */
  stageId: string | null
  /** This tab's LiveKit identity, the one its seat holds. */
  identity: string | null
  /** This tab is live as a host. Off, Studio mode reads off and a share stops. */
  live: boolean
  /** That stage's live members as the room metadata lists them (null before it lists stages): this seat's Studio mode. */
  members: readonly StageFace[] | null
  /** That stage's called source: a share that stops takes back a call on it. */
  calledIdentity: string | null
}

export interface HostSources {
  /** This tab's seat is in Studio mode: its camera is a 16:9 source, not a face. What it asked shows at once. */
  studio: boolean
  toggleStudio(): Promise<void>
  /** This browser can share a screen: `getDisplayMedia` and a pointer that aims (no phone or tablet). */
  canShare: boolean
  /** This tab publishes a screen share now. The browser's own Stop sharing ends it too. */
  sharing: boolean
  toggleShare(): Promise<void>
  /** Why the last toggle failed, in words for the dock; cleared by the next one. */
  error: string | null
}

const STUDIO_FAILED = 'We couldn’t switch Studio mode. Try again.'
const SHARE_FAILED = 'We couldn’t share your screen. Try again.'
const SHARE_BLOCKED = 'Allow screen recording for this browser in your computer’s settings, then try again.'

/**
 * A live host's own sources beside their face (docs/features/watch-experience.md, Host sources): Studio mode, which the
 * API keeps on the seat and the room metadata carries to every viewer, and a screen share, which LiveKit carries as a
 * track of its own (the host's grant allows it; a guest's doesn't). Either shows in the strip as a source the audience
 * picks, watches and the host calls. Use inside `WatchRoom` (the room's context).
 */
export function useHostSources({ stageId, identity, live, members, calledIdentity }: HostSourcesOptions): HostSources {
  const room = useRoomContext()
  const [error, setError] = useState<string | null>(null)

  // Studio mode: the room metadata's word on this seat. A toggle shows what it asked at once, until the metadata moves
  // from what it said when asked (or the API refuses).
  const roomStudio = Boolean(live && identity && members?.some((member) => member.identity === identity && member.studio))
  const [asked, setAsked] = useState<{ studio: boolean; from: boolean } | null>(null)
  if (asked && (!live || asked.from !== roomStudio)) setAsked(null)
  const studio = live && asked ? asked.studio : roomStudio
  const studioRef = useRef(studio)
  useLayoutEffect(() => {
    studioRef.current = studio
  })

  const toggleStudio = useCallback(async () => {
    if (!stageId || !identity) return
    const next = !studioRef.current
    setError(null)
    setAsked({ studio: next, from: roomStudio })
    try {
      await watchApi.setStudio(stageId, identity, next)
    } catch (failure) {
      setAsked(null)
      const refused = apiFailure(failure)
      setError(refused.status === 429 ? 'That’s a lot of tries. Wait a minute, then try again.' : STUDIO_FAILED)
    }
  }, [stageId, identity, roomStudio])

  // Screen share: whatever LiveKit publishes for this tab, which the browser's Stop sharing bar ends too (LiveKit
  // unpublishes a screen share whose track ends).
  const finePointer = useMediaQuery(FINE_POINTER)
  const canShare = finePointer && typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function'
  const [sharing, setSharing] = useState(() => room.localParticipant.isScreenShareEnabled)
  useEffect(() => {
    const participant = room.localParticipant
    const update = () => setSharing(participant.isScreenShareEnabled)
    update()
    participant.on(ParticipantEvent.LocalTrackPublished, update)
    participant.on(ParticipantEvent.LocalTrackUnpublished, update)
    return () => {
      participant.off(ParticipantEvent.LocalTrackPublished, update)
      participant.off(ParticipantEvent.LocalTrackUnpublished, update)
    }
  }, [room])

  const busyRef = useRef(false)
  const toggleShare = useCallback(async () => {
    if (busyRef.current) return
    busyRef.current = true
    setError(null)
    try {
      await room.localParticipant.setScreenShareEnabled(!room.localParticipant.isScreenShareEnabled, SCREEN_CAPTURE)
    } catch (failure) {
      // Closing the browser's picker is a choice, not a failure; the system refusing the browser is.
      if (failure instanceof DOMException && failure.name === 'NotAllowedError') {
        if (/system/i.test(failure.message)) setError(SHARE_BLOCKED)
      } else setError(SHARE_FAILED)
    } finally {
      busyRef.current = false
    }
  }, [room])

  // Off the stage, the share stops (the lowered permission would end it anyway), and so does one left on leaving.
  useEffect(() => {
    if (!live && room.localParticipant.isScreenShareEnabled) void room.localParticipant.setScreenShareEnabled(false).catch(() => {})
  }, [live, room])
  useEffect(() => () => void room.localParticipant.setScreenShareEnabled(false).catch(() => {}), [room])

  // A share that stops takes back the stage's call on it, so the audience isn't sent back to it by the next share.
  const callRef = useRef({ stageId, identity, live, calledIdentity })
  useLayoutEffect(() => {
    callRef.current = { stageId, identity, live, calledIdentity }
  })
  const wasSharing = useRef(sharing)
  useEffect(() => {
    const stopped = wasSharing.current && !sharing
    wasSharing.current = sharing
    const current = callRef.current
    if (!stopped || !current.live || !current.stageId || !current.identity) return
    if (current.calledIdentity === screenSource(current.identity)) void watchApi.callCamera(current.stageId, null).catch(() => {})
  }, [sharing])

  useEffect(() => {
    if (!live) setError(null)
  }, [live])

  // One object while nothing in it changes, so the page's memoised dock doesn't render again for nothing.
  return useMemo(
    () => ({ studio, toggleStudio, canShare, sharing, toggleShare, error }),
    [studio, toggleStudio, canShare, sharing, toggleShare, error],
  )
}
